import {
  assessVital,
  bmiFromScaled,
  formatBmi,
  checkBloodPressureOrder,
  isCriticalInterpretation,
  parseVitalValue,
  VITAL_SIGNS,
  VITAL_VALUE_ERRORS,
  type ObservationInterpretation,
  type VitalKey,
  type VitalRule,
} from '@wodi/domain';
import type {
  ClinicalAlertRule,
  ClinicalRulesView,
  TriageCategoryView,
  TriageView,
  VitalInput,
  VitalRuleView,
} from '@wodi/shared';

// The triage form's rules, checked while the nurse types (docs/plans/phase1-triage.md
// §4, §6.3). The server checks everything again on save and is the authority; this gives
// the same answer earlier so nobody is surprised by it.

/** The form's field order: the paper chart's (DESIGN.md §8), BP as one pair. */
export const FORM_VITALS: readonly VitalKey[] = [
  'body_weight',
  'body_height',
  'body_temperature',
  'heart_rate',
  'bp_systolic',
  'bp_diastolic',
  'respiratory_rate',
  'oxygen_saturation',
  'pain_score',
  'muac',
  'blood_glucose',
];

export type VitalTexts = Partial<Record<VitalKey, string>>;

/** The rules as the domain's scaled integers. */
export function domainRules(rules: readonly VitalRuleView[]): VitalRule[] {
  const scaled = (key: VitalKey, text: string | null): number | null => {
    if (text === null) return null;
    const parsed = parseVitalValue(key, text);
    return parsed.ok ? parsed.value.scaled : null;
  };
  return rules.map((r) => ({
    id: r.id,
    version: r.version,
    vitalKey: r.vitalKey,
    kind: r.kind,
    ageFromDays: r.ageFromDays,
    ageToDays: r.ageToDays,
    low: scaled(r.vitalKey, r.low),
    high: scaled(r.vitalKey, r.high),
    context: r.context,
    approval: r.approval,
    source: r.source,
  }));
}

export interface FieldJudgement {
  flag?: ObservationInterpretation;
  provisional: boolean;
  /** Why it can't be saved; shown under the field. Never logged or sent anywhere. */
  error?: string;
}

/** What to show under one field while typing. Empty means "not taken". */
export function judgeField(
  key: VitalKey,
  text: string,
  rules: readonly VitalRule[],
  ageDays: number | null,
  context: string | null,
): FieldJudgement {
  if (text.trim() === '') return { provisional: false };
  const definition = VITAL_SIGNS[key];
  const parsed = parseVitalValue(key, text);
  if (!parsed.ok) return { provisional: false, error: `${VITAL_VALUE_ERRORS[parsed.code]}.` };
  const assessed = assessVital(rules, {
    vitalKey: key,
    scaled: parsed.value.scaled,
    ageDays,
    context,
  });
  if (!assessed.ok) {
    if ('rule' in assessed) {
      return {
        provisional: assessed.rule.approval !== 'approved',
        // DESIGN.md §7: "Temperature 73 °C is not possible — check the value", without the dash.
        error: `${definition.label} ${text.trim()} ${definition.unitDisplay} is not possible. Check the value.`,
      };
    }
    return {
      provisional: false,
      error: 'The clinical rules for this measurement overlap. Ask the clinical lead to fix them.',
    };
  }
  return { flag: assessed.value.interpretation, provisional: assessed.value.provisional };
}

/** A blood pressure entered as one half only, or in the wrong order. */
export function bloodPressureError(texts: VitalTexts): string | null {
  const sys = texts.bp_systolic?.trim() ?? '';
  const dia = texts.bp_diastolic?.trim() ?? '';
  if (sys === '' && dia === '') return null;
  if (sys === '' || dia === '') return 'Enter both systolic and diastolic, or neither.';
  const s = parseVitalValue('bp_systolic', sys);
  const d = parseVitalValue('bp_diastolic', dia);
  if (!s.ok || !d.ok) return null;
  // TODO(clinical-verify): plan §13 C10.
  return checkBloodPressureOrder({ systolic: s.value.scaled, diastolic: d.value.scaled }).ok
    ? null
    : 'Systolic must be higher than diastolic. Check the values.';
}

/** Whether MUAC shows by default for this age (the clinical lead's setting, plan C5). */
export function muacShown(
  settings: ClinicalRulesView['settings'],
  ageDays: number | null,
): boolean {
  const { muacFromAgeDays: from, muacToAgeDays: to } = settings;
  if (from === null || to === null || ageDays === null) return false;
  return ageDays >= from && ageDays < to;
}

export interface PendingAlert {
  ruleId: ClinicalAlertRule;
  title: string;
  body: string;
  danger: boolean;
}

/**
 * The critical-range alert these values would raise, or null. Shared by triage and the
 * ward's vitals round (docs/plans/phase2-ward-rounds-nursing.md §1.1 item 4).
 */
export function criticalVitalsAlert(input: {
  texts: VitalTexts;
  glucoseContext: string | null;
  rules: readonly VitalRule[];
  ageDays: number | null;
}): PendingAlert | null {
  const critical = FORM_VITALS.filter((key) => {
    const text = input.texts[key] ?? '';
    const judged = judgeField(
      key,
      text,
      input.rules,
      input.ageDays,
      key === 'blood_glucose' ? input.glucoseContext : null,
    );
    return judged.flag !== undefined && isCriticalInterpretation(judged.flag);
  });
  if (critical.length === 0) return null;
  const names = critical.map((k) => VITAL_SIGNS[k].label).join(', ');
  return {
    ruleId: 'vitals.critical_range',
    title: `${names}: in the critical range.`,
    body: 'Tell the clinician now.',
    danger: true,
  };
}

/** The interruptive alerts this save would raise (plan §4); the server decides finally. */
export function pendingAlerts(input: {
  texts: VitalTexts;
  glucoseContext: string | null;
  rules: readonly VitalRule[];
  ageDays: number | null;
  settings: ClinicalRulesView['settings'];
  allergies: TriageView['allergies'];
  category: TriageCategoryView | undefined;
}): PendingAlert[] {
  const alerts: PendingAlert[] = [];
  const critical = criticalVitalsAlert(input);
  if (critical !== null) alerts.push(critical);
  if (input.allergies.state === 'not_recorded') {
    alerts.push({
      ruleId: 'triage.allergies_not_recorded',
      title: 'Allergies not recorded.',
      body: 'Ask the patient, then record allergies or No known allergies.',
      danger: false,
    });
  }
  const threshold = input.settings.weightRequiredBelowAgeDays;
  if (
    threshold !== null &&
    input.ageDays !== null &&
    input.ageDays < threshold &&
    (input.texts.body_weight ?? '').trim() === ''
  ) {
    alerts.push({
      ruleId: 'triage.weight_missing_child',
      title: 'No weight recorded.',
      body: "Children's doses depend on weight.",
      danger: false,
    });
  }
  if (input.category?.requiresImmediateAttention === true) {
    alerts.push({
      ruleId: 'triage.immediate_attention',
      title: `${input.category.display} needs a clinician now.`,
      body: 'Tell the clinician before sending the patient to a queue.',
      danger: false,
    });
  }
  return alerts;
}

/** BMI from what is typed, for display only; never sent or stored (plan §2.1). */
export function liveBmi(texts: VitalTexts): string | null {
  const weight = parseVitalValue('body_weight', texts.body_weight ?? '');
  const height = parseVitalValue('body_height', texts.body_height ?? '');
  if (!weight.ok || !height.ok) return null;
  const bmi = bmiFromScaled(weight.value.scaled, height.value.scaled);
  return bmi === null ? null : formatBmi(bmi);
}

/** The vitals to send: every field with a value. */
export function vitalInputs(texts: VitalTexts, glucoseContext: string | null): VitalInput[] {
  return FORM_VITALS.flatMap((key) => {
    const value = texts[key]?.trim() ?? '';
    if (value === '') return [];
    return [
      {
        key,
        value,
        ...(key === 'blood_glucose' && glucoseContext !== null ? { context: glucoseContext } : {}),
      },
    ];
  });
}

/** Messages for alerts the server raised that the form didn't foresee. */
export const ALERT_FALLBACK: Record<ClinicalAlertRule, Omit<PendingAlert, 'ruleId'>> = {
  'vitals.critical_range': {
    title: 'A value is in the critical range.',
    body: 'Tell the clinician now.',
    danger: true,
  },
  'triage.allergies_not_recorded': {
    title: 'Allergies not recorded.',
    body: 'Ask the patient, then record allergies or No known allergies.',
    danger: false,
  },
  'triage.weight_missing_child': {
    title: 'No weight recorded.',
    body: "Children's doses depend on weight.",
    danger: false,
  },
  'triage.immediate_attention': {
    title: 'This category needs a clinician now.',
    body: 'Tell the clinician before sending the patient to a queue.',
    danger: false,
  },
};
